import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { Tooltip, useTheme } from "@mui/material";
import { AI_MODE, AI_MODE_LABELS } from "../../utils/aiInterviewApi";

// Cells shared by the Interviews page and the candidate's interview list

const zoneNamePart = (date, timeZone, timeZoneName) => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};

const utcOffsetLabel = (date, timeZone) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date).map((part) => [part.type, part.value])
  );
  const zonedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute)
  );
  const diffMinutes = Math.round((zonedAsUtc - date.getTime()) / 60000);
  const sign = diffMinutes >= 0 ? "+" : "-";
  const absolute = Math.abs(diffMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, "0");
  const minutes = String(absolute % 60).padStart(2, "0");
  return `UTC${sign}${hours}:${minutes}`;
};

export const InterviewTime = ({ interview }) => {
  const theme = useTheme();
  if (!interview.start_utc) {
    return <span className="text-sm" style={{ color: theme.palette.text.secondary || "#6b7280" }}>Pending</span>;
  }
  const start = new Date(interview.start_utc);
  if (Number.isNaN(start.getTime())) {
    return <span className="text-sm">Pending</span>;
  }
  const timeZone = interview.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  let parts;
  try {
    parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-GB", {
        timeZone,
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).formatToParts(start).map((part) => [part.type, part.value])
    );
  } catch {
    return <span className="text-sm">{interview.start_local || interview.start_utc}</span>;
  }
  const startClock = `${parts.hour}:${parts.minute}`;
  let endClock = "";
  if (interview.end_utc) {
    const end = new Date(interview.end_utc);
    if (!Number.isNaN(end.getTime())) {
      try {
        const endParts = Object.fromEntries(
          new Intl.DateTimeFormat("en-GB", {
            timeZone,
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
          }).formatToParts(end).map((part) => [part.type, part.value])
        );
        endClock = `${endParts.hour}:${endParts.minute}`;
      } catch {
        endClock = "";
      }
    }
  }
  const abbreviation = zoneNamePart(start, timeZone, "short");
  const offset = utcOffsetLabel(start, timeZone);
  const zoneBits = [
    abbreviation && !/^(GMT|UTC)/.test(abbreviation) ? abbreviation : "",
    timeZone,
    offset,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-0.5 leading-snug min-w-[12rem]">
      <span className="text-sm font-medium">{parts.weekday}, {parts.day} {parts.month} {parts.year}</span>
      <span className="text-sm">{endClock ? `${startClock} – ${endClock}` : startClock}</span>
      <span className="text-xs" style={{ color: theme.palette.text.secondary || "#6b7280" }}>{zoneBits.join(" · ")}</span>
    </div>
  );
};

// Tinted fill with a stronger border of the same hue, like the skill chips and AI badge
const statusHighlight = {
  pending: { backgroundColor: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" },
  scheduled: { backgroundColor: "#DBEAFE", borderColor: "#93C5FD", color: "#1E40AF" },
  rescheduled: { backgroundColor: "#EDE9FE", borderColor: "#C4B5FD", color: "#5B21B6" },
  cancelled: { backgroundColor: "#FEE2E2", borderColor: "#FCA5A5", color: "#991B1B" },
  completed: { backgroundColor: "#DCFCE7", borderColor: "#86EFAC", color: "#166534" },
};
const defaultStatusStyle = { backgroundColor: "#F3F4F6", borderColor: "#D1D5DB", color: "#374151" };

// Backend "pending_candidate_selection" reads as "pending"
export const StatusHighlight = ({ status }) => {
  if (!status) return "—";
  const key = status.startsWith("pending") ? "pending" : status;
  return (
    <span
      className="inline-flex items-center whitespace-nowrap capitalize rounded-full border px-2.5 py-0.5 text-xs font-medium"
      style={statusHighlight[key] || defaultStatusStyle}
    >
      {key.replace(/_/g, " ")}
    </span>
  );
};

// Same tints as the status pills, so avatars sit naturally next to them
const AVATAR_TONES = [
  { backgroundColor: "#DBEAFE", color: "#1D4ED8" },
  { backgroundColor: "#FCE7F3", color: "#9D174D" },
  { backgroundColor: "#DCFCE7", color: "#166534" },
  { backgroundColor: "#FEF3C7", color: "#92400E" },
  { backgroundColor: "#EDE9FE", color: "#6D28D9" },
];
const MAX_VISIBLE_AVATARS = 1;

const initialsOf = (name) => {
  const words = name.trim().split(/\s+/);
  return (words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2)).toUpperCase();
};

// The same name always gets the same colour across rows
const avatarToneOf = (name) => {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) % 997;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

export const PanelistAvatar = ({ name, size = 32, stacked = false }) => (
  <span
    aria-hidden="true"
    className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold"
    style={{
      ...avatarToneOf(name),
      width: size,
      height: size,
      fontSize: size < 26 ? "0.625rem" : "0.75rem",
      // Outside ring keeps every avatar the full size; a border would eat into it
      boxShadow: stacked ? "0 0 0 2px #ffffff" : undefined,
    }}
  >
    {initialsOf(name)}
  </span>
);

// Overlapping avatars (names on hover/focus/tap), even for a single panelist.
// `aiMode` is the interview's ai_mode: an AI interviewer interview can have no panelists.
export const PanelistNames = ({ names = [], aiMode = null }) => {
  const theme = useTheme();

  if (!names.length) {
    if (aiMode !== AI_MODE.AI_INTERVIEWER) return "—";
    return (
      <span className="text-sm" style={{ color: theme.palette.text.secondary }}>
        AI interviewer
      </span>
    );
  }

  const visibleNames = names.slice(0, MAX_VISIBLE_AVATARS);
  const hiddenCount = names.length - visibleNames.length;

  return (
    <Tooltip
      placement="bottom-start"
      enterTouchDelay={0}
      leaveTouchDelay={4000}
      title={
        <div className="flex flex-col gap-2 py-0.5">
          {names.map((name) => (
            <span key={name} className="flex items-center gap-2">
              <PanelistAvatar name={name} size={22} />
              {name}
            </span>
          ))}
        </div>
      }
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.text.main,
            border: `1px solid ${theme.palette.secondary.main}`,
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.14)",
            fontFamily: "Instrument Sans, sans-serif",
            fontSize: "0.875rem",
            fontWeight: 400,
            px: 1.5,
            py: 1,
            maxWidth: 260,
          },
        },
      }}
    >
      <span
        tabIndex={0}
        aria-label={`Panelists: ${names.join(", ")}`}
        className="inline-flex items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
      >
        {visibleNames.map((name, index) => (
          <span key={name} className={index > 0 ? "-ml-2" : ""}>
            <PanelistAvatar name={name} stacked />
          </span>
        ))}
        {hiddenCount > 0 && (
          <span
            aria-hidden="true"
            className="-ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
            style={{
              backgroundColor: theme.palette.secondary.v2,
              color: theme.palette.text.icon,
              boxShadow: `0 0 0 2px #ffffff, inset 0 0 0 1px ${theme.palette.secondary.v3}`,
            }}
          >
            +{hiddenCount}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

// The interview's AI mode under its status, for interviews whose AI session isn't shown
export const AIModeCaption = ({ aiMode }) =>
  AI_MODE_LABELS[aiMode] ? <span className="block mt-1 text-xs text-gray-600">{AI_MODE_LABELS[aiMode]}</span> : null;

// Feedback results use the same pill treatment as the interview statuses
export const RESULT_STYLES = {
  cleared: { label: "Cleared", backgroundColor: "#DCFCE7", borderColor: "#86EFAC", color: "#166534", dot: "#16A34A" },
  neutral: { label: "Neutral", backgroundColor: "#F3F4F6", borderColor: "#D1D5DB", color: "#374151", dot: "#9CA3AF" },
  rejected: { label: "Rejected", backgroundColor: "#FEE2E2", borderColor: "#FCA5A5", color: "#991B1B", dot: "#DC2626" },
};

export const ResultPill = ({ result }) => {
  const style = RESULT_STYLES[result];
  if (!style) return null;
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium"
      style={{ backgroundColor: style.backgroundColor, borderColor: style.borderColor, color: style.color }}
    >
      {style.label}
    </span>
  );
};

const StarIcon = ({ className, color }) => (
  <svg viewBox="0 0 20 20" fill={color} aria-hidden="true" className={className}>
    <path d="M10 1.8l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.42 5.06 17.01 6 11.51 2 7.61l5.53-.8L10 1.8z" />
  </svg>
);

// One decimal, without a trailing ".0" (5 stays "5", 4.2 stays "4.2")
const scoreText = (value, withScale) => {
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return withScale ? `${text}/5` : text;
};

// Green once the score is strong; gray while it's lower or still coming in; red when any panelist rejected
const FEEDBACK_CHIP_TONES = {
  positive: { backgroundColor: "#E7F6EC", star: "#25a425", title: "#111827", caption: "#023902" },
  neutral: { backgroundColor: "#F3F4F6", star: "#6B7280", title: "#111827", caption: "#374151" },
  rejected: { backgroundColor: "#FEE2E2", star: "#ff0000", title: "#991B1B", caption: "#B91C1C" },
};

// Table cell from the server's summary (GET /interviews/{id}/feedback):
// "Add feedback" before any panelist has submitted, then the score with Cleared, Neutral, or Rejected under it
export const FeedbackSummaryCell = ({ summary, loaded, canAdd, onOpen }) => {
  const theme = useTheme();
  const submitted = summary?.feedback_count || 0;

  if (!loaded) {
    return <span className="text-sm" style={{ color: theme.palette.text.secondary }}>…</span>;
  }
  if (!submitted) {
    if (!canAdd) return "—";
    return (
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex h-[26px] items-center whitespace-nowrap rounded border bg-white px-2.5 text-xs font-medium hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
        style={{ borderColor: theme.palette.secondary.v4, color: theme.palette.info.main }}
      >
        Add feedback
      </button>
    );
  }

  const average = typeof summary.average_rating === "number" ? summary.average_rating : null;
  const total = Math.max(summary.panelist_count || 0, submitted);
  const resultCounts = Object.keys(RESULT_STYLES)
    .map((result) => ({ result, count: summary.result_counts?.[result] || 0 }))
    .filter((entry) => entry.count > 0);
  const unanimous = resultCounts.length === 1 ? resultCounts[0].result : null;
  const pending = submitted < total;
  // The line under the score is only the result: Cleared, Neutral, or Rejected
  const resultLine = resultCounts.map(({ result }) => RESULT_STYLES[result].label).join(" · ");
  const resultSummary = resultCounts
    .map(({ result, count }) => `${count} ${RESULT_STYLES[result].label.toLowerCase()}`)
    .join(", ");

  const title = average !== null ? scoreText(average, true) : (resultLine || "Feedback");
  const starOnTitle = average !== null;
  const caption = starOnTitle ? resultLine : "";
  const submittedLabel = `${submitted}/${total} submitted`;

  // Red only when every result is a rejection; split results like "Cleared · Rejected" stay neutral
  const toneKey = unanimous === "rejected"
    ? "rejected"
    : !pending && average !== null && average >= 4.5 && unanimous !== "neutral" && resultCounts.length <= 1
      ? "positive"
      : "neutral";
  const tone = FEEDBACK_CHIP_TONES[toneKey];
  const averageLabel = average !== null ? scoreText(average, false) : "";

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Feedback: ${[title, caption].filter(Boolean).join(", ")}. ${submitted} of ${total} panelists submitted${average !== null ? `, average ${averageLabel} out of 5` : ""}${resultSummary ? `, ${resultSummary}` : ""}. Open feedback`}
      title="Open feedback"
      className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-lg py-1.5 pl-2.5 pr-1.5 text-left transition hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
      style={{ backgroundColor: tone.backgroundColor }}
    >
      <span className="flex flex-col" aria-hidden="true">
        <span className="inline-flex items-center gap-1 text-[13px] font-semibold leading-4" style={{ color: tone.title }}>
          {starOnTitle && <StarIcon className="h-3.5 w-3.5 shrink-0" color={tone.star} />}
          {title}
          <span className="pl-1 text-[11px] leading-4 font-light" style={{ color: tone.caption }}>{submittedLabel}</span>
        </span>
        {caption && (
          <span className="text-[11px] leading-4 font-bold" style={{ color: tone.caption }}>
            {caption}
          </span>
        )}
      </span>
      <ChevronRightIcon className="h-4 w-4 shrink-0" style={{ color: "#333333" }} aria-hidden="true" />
    </button>
  );
};
